import { FC } from 'react';

import { PlusIcon } from '../../../../../assets/icons/index';
import { PlusIconVariant } from '../../../../../assets/icons/plus-icon';
import lockedWithKey from '../../../../../assets/images/locked-with-key.png';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';

/**
 * A dedicated screen, not a table with headers and no rows: an Admin opening the modal for the
 * first time should be told what to do, not shown eight empty columns.
 *
 * The panel and the ring around the illustration are the house empty state — `empty-vault.tsx` and
 * `empty-pools.tsx` are the same two: `radial-gradient-border-yellow` paints the fading hairline as
 * a conic gradient on the border box, and the white `p-3` collar carries the inset glow. Twelve
 * pixels of collar around a 112px disc is the 136px ring the design draws.
 */
export const AccessEmptyState: FC<{ isEditable: boolean; onInvite: () => void }> = ({
  isEditable,
  onInvite,
}) => (
  // `w-full` against the body's `items-start`: without it this box shrinks to the panel and parks
  // itself at the left padding, and centring inside it only ever centres the text.
  <div className="flex w-full flex-1 flex-col items-center justify-center">
    <div className="radial-gradient-border-yellow centered flex w-[393.33px] flex-col items-center justify-center gap-6 rounded-2xl px-20 py-8">
      <div className="flex flex-col items-center gap-10">
        <div
          className="rounded-full bg-white p-3"
          style={{ boxShadow: '0px 0px 8px 0px #DC680321 inset' }}
        >
          <div
            className="centered flex size-28 items-center gap-3 rounded-full"
            style={{
              background:
                'linear-gradient(180deg, rgba(254, 200, 75, 0.13) 0%, rgba(220, 104, 3, 0.13) 100%)',
            }}
          >
            <div className="size-[56px] object-cover">
              <img alt="" src={lockedWithKey} />
            </div>
          </div>
        </div>
        {/* One line each, as drawn: the copy runs past the panel's padding rather than wrapping. */}
        <div className="flex flex-col items-center gap-2 whitespace-nowrap">
          <p className="text-xl font-semibold leading-8 text-gray-700">
            <AppFormattedMessage id="NO_ONE_HAS_ACCESS_YET" />
          </p>
          <p className="text-center text-xs font-medium leading-[18px] text-gray-500">
            <AppFormattedMessage id="INVITE_PEOPLE_TO_GIVE_ACCESS" />
          </p>
        </div>
      </div>
      <Button
        className="h-9 w-fit gap-1 py-1.5 pl-4 pr-5"
        disabled={!isEditable}
        onClick={onInvite}
        type="button"
      >
        <PlusIcon className="size-5" iconColor="#ffffff" variant={PlusIconVariant.VAULT} />
        <AppFormattedMessage id="INVITE_PEOPLE" />
      </Button>
    </div>
  </div>
);
